import { createContext, useContext, useState, useCallback, useRef } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { AlertCircle, PhoneOff } from "lucide-react";

interface NavigationGuardContextType {
  guardActive: boolean;
  guardMessage: string;
  pendingPath: string | null;
  onCallActive: boolean;
  setGuard: (active: boolean, message?: string, opts?: { onConfirm?: () => Promise<void>; onCallActive?: boolean }) => void;
  requestNavigation: (path: string) => boolean;
  confirmNavigation: () => void;
  cancelNavigation: () => void;
}

const NavigationGuardContext = createContext<NavigationGuardContextType>({
  guardActive: false,
  guardMessage: "",
  pendingPath: null,
  onCallActive: false,
  setGuard: () => {},
  requestNavigation: () => true,
  confirmNavigation: () => {},
  cancelNavigation: () => {},
});

export function NavigationGuardProvider({ children }: { children: React.ReactNode }) {
  const [guardActive, setGuardActive] = useState(false);
  const [guardMessage, setGuardMessage] = useState("");
  const [pendingPath, setPendingPath] = useState<string | null>(null);
  const [onCallActive, setOnCallActive] = useState(false);
  const onConfirmRef = useRef<(() => Promise<void>) | undefined>();
  const navigateRef = useRef<((path: string) => void) | undefined>();

  const setGuard = useCallback((active: boolean, message?: string, opts?: { onConfirm?: () => Promise<void>; onCallActive?: boolean }) => {
    setGuardActive(active);
    setGuardMessage(message || "");
    onConfirmRef.current = opts?.onConfirm;
    setOnCallActive(opts?.onCallActive || false);
    if (!active) {
      setPendingPath(null);
    }
  }, []);

  const requestNavigation = useCallback((path: string): boolean => {
    if (!guardActive) return true;
    setPendingPath(path);
    return false;
  }, [guardActive]);

  const confirmNavigation = useCallback(async () => {
    const path = pendingPath;
    if (onConfirmRef.current) {
      try {
        await onConfirmRef.current();
      } catch {}
    }
    setGuardActive(false);
    setGuardMessage("");
    setOnCallActive(false);
    onConfirmRef.current = undefined;
    setPendingPath(null);
    if (path) {
      window.location.href = path;
    }
  }, [pendingPath]);

  const cancelNavigation = useCallback(() => {
    setPendingPath(null);
  }, []);

  return (
    <NavigationGuardContext.Provider value={{
      guardActive,
      guardMessage,
      pendingPath,
      onCallActive,
      setGuard,
      requestNavigation,
      confirmNavigation,
      cancelNavigation,
    }}>
      {children}
    </NavigationGuardContext.Provider>
  );
}

export function useNavigationGuard() {
  return useContext(NavigationGuardContext);
}

export function NavigationGuardDialog() {
  const { pendingPath, guardMessage, onCallActive, confirmNavigation, cancelNavigation } = useNavigationGuard();

  if (!pendingPath) return null;

  return (
    <AlertDialog open={!!pendingPath} onOpenChange={(open) => { if (!open) cancelNavigation(); }}>
      <AlertDialogContent data-testid="navigation-guard-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2" data-testid="navigation-guard-title">
            {onCallActive ? (
              <>
                <PhoneOff className="h-5 w-5 text-destructive" />
                Active Call in Progress
              </>
            ) : (
              <>
                <AlertCircle className="h-5 w-5 text-orange-500" />
                Leave Campaign?
              </>
            )}
          </AlertDialogTitle>
          <AlertDialogDescription data-testid="navigation-guard-message">
            {onCallActive
              ? "You are currently on an active call. Please end the call before navigating away."
              : guardMessage || "Your assigned contacts will be redistributed to other active agents in the campaign."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="button-navigation-stay">
            {onCallActive ? "OK" : "Stay"}
          </AlertDialogCancel>
          {!onCallActive && (
            <AlertDialogAction
              data-testid="button-navigation-leave"
              onClick={(e) => {
                e.preventDefault();
                confirmNavigation();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Leave & Redistribute
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
